<template>
  <UISidePanel v-model="modelValue" hide-buttons @close="modelValue = false">
    <div class="flex flex-col text-black-70 pb-24 tablet:pb-0" :style="{ paddingTop: headerHeight + 'px' }">
      <div class="bg-[#F8F7F3] px-8 pt-5 pb-4">
        <div class="flex h-[89px] flex-row items-center gap-3">
          <UIAvatar
            :src="authStore.user?.profileImageUrl"
            :user="authStore.user || undefined"
            :height="48"
            :width="48"
            no-border
            class="!text-lg"
          />
          <div class="flex flex-col">
            <h3 class="font-bold text-black-80">{{ authStore.user?.firstName }} {{ authStore.user?.lastName }}</h3>
            <span class="text-xs text-black-40">{{ authStore.user?.email }}</span>
          </div>
        </div>
      </div>

      <NuxtLink
        :to="localePath('/profile#personal')"
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click.capture="modelValue = false"
      >
        <UIIcon name="user" />
        {{ $t('navigation.menu.profile') }}
      </NuxtLink>
      <hr class="my-1 w-full border-black-10" />
      <div>
        <button
          class="flex h-[72px] w-full cursor-pointer flex-row items-center gap-3 px-8"
          @click="isLanguageExpanded = !isLanguageExpanded"
        >
          <UIIcon name="world" />
          <span class="capitalize">{{ locale }}</span>
          <UIIcon
            name="chevron-big-right"
            class="ml-auto transition-transform duration-200"
            :class="{ 'rotate-90': isLanguageExpanded }"
          />
        </button>
        <div
          v-if="isLanguageExpanded"
          class="overflow-hidden transition-all duration-200"
        >
          <button
            v-for="lang in locales"
            :key="lang.code"
            class="flex h-[56px] w-full cursor-pointer flex-row items-center gap-3 px-8 pl-16 text-sm"
            :class="locale === lang.code ? 'bg-gold-primary-10 font-semibold' : 'text-black-70'"
            @click="setLanguage(lang.code)"
          >
            <UIIcon
              v-if="locale === lang.code"
              name="check"
              class="text-gold-primary-100"
            />
            <span :class="locale === lang.code ? 'text-black-100' : ''">
              {{ lang.name }}
            </span>
          </button>
        </div>
      </div>
      <hr class="my-1 w-full border-black-10" />
      <NuxtLink
        :to="localePath('/profile#settings')"
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click.capture="modelValue = false"
      >
        <UIIcon name="settings" />
        {{ $t('navigation.menu.settings') }}
      </NuxtLink>
      <hr class="my-1 w-full border-black-10" />
      <button
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click="showBookCallPopup"
      >
        <UIIcon name="calendar" />
        {{ $t('navigation.menu.bookAppointment') }}
      </button>
      <hr class="my-1 w-full border-black-10" />
      <button
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click="showHelpPopup"
      >
        <UIIcon name="shield" />
        {{ $t('navigation.menu.helpSupport') }}
      </button>
      <hr v-if="authStore.getIsAdmin" class="my-1 w-full border-black-10" />
      <NuxtLink
        v-if="authStore.getIsAdmin"
        :to="localePath('/admin/users')"
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click.capture="modelValue = false"
      >
        <UIIcon name="id" />
        {{ $t('navigation.menu.admin') }}
      </NuxtLink>
      <hr class="my-1 w-full border-black-10" />
      <button
        class="flex h-[72px] cursor-pointer flex-row items-center gap-3 px-8"
        @click="closeAndLogout()"
      >
        <UIIcon name="log-out" />
        {{ $t('navigation.menu.logout') }}
      </button>
    </div>
  </UISidePanel>
</template>

<script setup lang="ts">
import { POPUPS } from '@/components/dynamic/maps'
import { useTolgee } from '@tolgee/vue'

const uiStore = useUIStore()
const authStore = useAuthStore()
const modelValue = defineModel<boolean>()
const route = useRoute()

const { locale, locales, setLocale } = useI18n()
const localePath = useLocalePath()
const tolgee = useTolgee(['language'])

const isLanguageExpanded = ref(false)

const { getBannerConfig } = useMobileBanner()

const headerHeight = computed(() => {
  const baseHeaderHeight = 20
  const bannerConfig = getBannerConfig()

  if (bannerConfig) {
    return baseHeaderHeight + 60
  }

  return baseHeaderHeight
})

const { mutateAsync: logout } = useLogout()

const closeAndLogout = async () => {
  logout()
  modelValue.value = false
}

const showHelpPopup = () => {
  uiStore.showPopup(POPUPS.HELP_POPUP)
}

const showBookCallPopup = () => {
  uiStore.showPopup(POPUPS.BOOK_A_CALL, {
    standaloneBooking: true,
  })
}

const setLanguage = async (langCode: string) => {
  if (locale.value === langCode) {
    isLanguageExpanded.value = false
    return
  }

  setLocale(langCode)
  await tolgee.value.changeLanguage(langCode)

  setTimeout(() => {
    window.location.reload()
  }, 100)
}

watch(route, () => {
  if (modelValue.value) {
    modelValue.value = false
  }
})
</script>

<style scoped></style>
